import React, { FC } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SetNewPasswordForm,
  SetNewPasswordFormValues,
} from 'src/components/Forms/SetNewPasswordForm';
import { setNewPasswordFormSchema } from 'src/components/Forms/validationSchemas';
import { Modal } from 'src/components/Modals';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { SET_NEW_PASSWORD } from 'src/utils/gql/mutations';
import { Mutation, MutationSetNewPasswordArgs } from 'src/utils/gql/types';
import { setToken } from 'src/utils/localStorage';

export const SetNewPassword: FC = () => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const secret = searchParams.get('secret') || '';

  const formMethods = useForm<SetNewPasswordFormValues>({
    mode: 'onChange',
    resolver: yupResolver(setNewPasswordFormSchema),
    criteriaMode: 'all',
    defaultValues: {
      password: '',
      repeatPassword: '',
    },
  });

  const [setNewPassword, { loading }] = useMutation<
    Pick<Mutation, 'setNewPassword'>,
    MutationSetNewPasswordArgs
  >(SET_NEW_PASSWORD, {
    onCompleted: ({ setNewPassword }) => {
      setToken(setNewPassword);
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
    },
    onError: (err) => {
      const { graphQLErrors, message: errorText } = err as ApolloError;
      const message =
        graphQLErrors && graphQLErrors.length > 0
          ? graphQLErrors[0].message
          : errorText;

      snackbar(message);
    },
  });

  const handleSubmit: SubmitHandler<SetNewPasswordFormValues> = ({
    password,
  }) => {
    setNewPassword({ variables: { newPassword: password, secret } });
  };

  return (
    <Modal open title="Kipify" hideBackdrop>
      <>
        <Typography
          variant="h2"
          textAlign="center"
          mb={isSmallScreen ? '25px' : '40px'}
        >
          Set New Password
        </Typography>
        <FormProvider {...formMethods}>
          <SetNewPasswordForm onSubmit={handleSubmit} loading={loading} />
        </FormProvider>
      </>
    </Modal>
  );
};
